import React, { useState } from 'react';
import cx from 'classnames';
import s from './s.module.less';
import OnboardingStepContainer from '../OnboardingStepContainer';
import ActionButtons from '../ActionButtons';
import { HELP_TYPE_OPTIONS, INTENT_ORDER, INTENT_CART, EHelpType, parseHelpTypes } from '../../helpType';

interface HelpTypeStepProps {
    onHelpTypeChange?: (helpType: string) => void;
    onBack?: () => void;
    currentStep?: number;
    totalSteps?: number;
    initialHelpType?: string;
}

export default function HelpTypeStep({
    onHelpTypeChange,
    onBack,
    currentStep = 2,
    totalSteps = 8,
    initialHelpType = '',
}: HelpTypeStepProps) {
    const [selected, setSelected] = useState<Set<string>>(() => {
        // parseHelpTypes 内部已处理老数据兼容（credentialing-billing → credentialing, all → ehr）
        return new Set(parseHelpTypes(initialHelpType));
    });
    const [fitOpen, setFitOpen] = useState<Record<string, boolean>>({});

    const toggleIntent = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleAll = () => {
        if (selected.size === HELP_TYPE_OPTIONS.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(HELP_TYPE_OPTIONS.map((o) => o.id)));
        }
    };

    const toggleFit = (key: string) => {
        setFitOpen((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleNext = () => {
        if (selected.size === 0) return;
        const value = INTENT_ORDER.filter((k) => selected.has(k)).join(',');
        onHelpTypeChange?.(value);
    };

    const isAllSelected = selected.size === HELP_TYPE_OPTIONS.length;
    const sortedSelected = INTENT_ORDER.filter((k) => selected.has(k));
    const showEhrNote =
        (selected.has(EHelpType.CREDENTIALING) || selected.has(EHelpType.INSURANCE_BILLING)) &&
        !selected.has(EHelpType.EHR);

    return (
        <>
            <OnboardingStepContainer
                currentStep={currentStep}
                totalSteps={totalSteps}
                title="How can Klarity help your practice?"
                subtitle="Start with what you need. You can always add more later."
                showBackButton
                onBack={onBack}
            >
                <div className={s.selectAllRow}>
                    <span className={s.selectAllBtn} onClick={toggleAll}>
                        {isAllSelected ? 'Clear all' : 'Select all'}
                    </span>
                </div>
                <div className={s.intentLayout}>
                    <div className={s.intentMain}>
                        <div className={s.optionsContainer}>
                            {HELP_TYPE_OPTIONS.map((option) => {
                                const isSelected = selected.has(option.id);
                                return (
                                    <div
                                        key={option.id}
                                        className={cx(s.option, isSelected && s.selected)}
                                        role="checkbox"
                                        aria-checked={isSelected}
                                        tabIndex={0}
                                        onClick={() => toggleIntent(option.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === ' ' || e.key === 'Enter') {
                                                e.preventDefault();
                                                toggleIntent(option.id);
                                            }
                                        }}
                                    >
                                        <div className={cx(s.checkIcon, isSelected && s.checkIconActive)}>
                                            {isSelected && (
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                        <div className={s.optionContent}>
                                            <div className={s.optionTitleRow}>
                                                <span className={s.optionTitle}>{option.title}</span>
                                                {option.badge && (
                                                    <span
                                                        className={cx(
                                                            s.badge,
                                                            option.badge.type === 'free' ? s.badgeFree : s.badgePopular,
                                                        )}
                                                    >
                                                        {option.badge.text}
                                                    </span>
                                                )}
                                            </div>
                                            <span className={s.optionDesc}>{option.description}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {sortedSelected.length > 0 && (
                        <div className={s.intentPanelSlot}>
                            <aside className={s.intentPanel}>
                                <div className={s.panelEyebrow}>What you&apos;re getting</div>
                                <ul className={s.cartList}>
                                    {sortedSelected.map((key) => {
                                        const item = INTENT_CART[key];
                                        if (!item) return null;
                                        return (
                                            <li key={key} className={s.cartItem}>
                                                <svg className={s.cartCheck} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M5 13l4 4L19 7" />
                                                </svg>
                                                <div>
                                                    <div className={s.cartName}>{item.name}</div>
                                                    <div className={s.cartDesc}>{item.desc}</div>
                                                    {item.fit.length > 0 && (
                                                        <>
                                                            <div
                                                                className={cx(s.fitToggle, fitOpen[key] && s.fitToggleOpen)}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleFit(key);
                                                                }}
                                                            >
                                                                Who is this for?
                                                                <svg className={s.fitChev} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <polyline points="6 9 12 15 18 9" />
                                                                </svg>
                                                            </div>
                                                            {fitOpen[key] && (
                                                                <ul className={s.fitList}>
                                                                    {item.fit.map((f) => (
                                                                        <li key={f}>{f}</li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                                {showEhrNote && (
                                    <div className={s.panelNote}>
                                        Keeping your current EHR? No problem. Klarity{' '}
                                        {selected.has(EHelpType.CREDENTIALING) && selected.has(EHelpType.INSURANCE_BILLING)
                                            ? 'Credentialing and Billing work'
                                            : `${selected.has(EHelpType.CREDENTIALING) ? 'Credentialing' : 'Billing'} works`}{' '}
                                        with whatever you already use.
                                    </div>
                                )}
                            </aside>
                        </div>
                    )}
                </div>
            </OnboardingStepContainer>

            <ActionButtons
                showBack
                showNext
                nextDisabled={selected.size === 0}
                currentStep={currentStep}
                onBack={onBack}
                onNext={handleNext}
            />
        </>
    );
}
